<!--
  WorkFileUploader.vue — document/image uploader for Work attachments.

  Posts to /api/media/ (the same S3 endpoint SessionHistory + EngagementMedia use),
  with the FULL backend capability: 50MB and the backend's complete ALLOWED_EXTENSIONS
  set (images + doc/docx/pdf/ppt/pptx/xls/xlsx/txt). Replaces the legacy MediaUploader,
  which was hardcoded to 5MB and images-only — far narrower than /api/media/ allows.
  Emits `file-uploaded` with { key } once the upload succeeds.
-->
<template>
  <div>
    <div class="gap-2 mb-2">
      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPT"
        class="hidden"
        @change="handleFileSelected"
      />
      <button
        class="bg-primary-500 text-white px-2 py-0.5 rounded text-[11px] font-medium"
        :disabled="uploading || disabled"
        @click="$refs.fileInput.click()"
      >
        Choose File
      </button>
      <span v-if="selectedFile" class="inline-flex bg-gray-100 rounded text-xs px-2 py-0.5 ml-1.5">
        {{ selectedFile.name }}
      </span>
    </div>

    <div v-if="selectedFile" class="gap-1.5 flex items-center">
      <button
        :disabled="uploading"
        class="bg-green-600 text-white px-2 py-0.5 rounded text-[11px] font-medium flex items-center gap-1"
        @click="uploadFile"
      >
        <Spinning_Loader v-if="uploading" />
        <span>{{ uploading ? 'Uploading...' : 'Upload' }}</span>
      </button>
      <button
        :disabled="uploading"
        class="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-[11px] font-medium"
        @click="cancelUpload"
      >
        Cancel
      </button>
    </div>

    <div v-if="uploadError" class="mt-1 text-red-500 text-xs">
      {{ uploadError }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue';

// Keep in sync with backend MediaView ALLOWED_EXTENSIONS + MEDIA_UPLOAD_MAX_MB
// (api/views.py) and nginx client_max_body_size.
const ACCEPT = '.jpg,.jpeg,.png,.doc,.docx,.pdf,.ppt,.pptx,.xls,.xlsx,.txt';
const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'doc', 'docx', 'pdf', 'ppt', 'pptx', 'xls', 'xlsx', 'txt'];
const MAX_MB = 50;

export default {
  name: 'WorkFileUploader',
  components: { Spinning_Loader },
  props: {
    relatedObjectId: { type: [Number, String], required: true },
    relatedObjectContentTypeModel: { type: String, default: 'work' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['file-uploaded'],
  data() {
    return {
      ACCEPT,
      selectedFile: null,
      uploading: false,
      uploadError: '',
    };
  },
  methods: {
    handleFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        this.uploadError = `Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}`;
        return;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        this.uploadError = `File size exceeds ${MAX_MB}MB limit`;
        return;
      }
      this.selectedFile = file;
      this.uploadError = '';
    },

    async uploadFile() {
      if (!this.selectedFile) return;
      this.uploading = true;
      this.uploadError = '';
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        formData.append('object_type', this.relatedObjectContentTypeModel);
        formData.append('object_id', this.relatedObjectId);

        const response = await axios.post('/api/media/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
          this.$emit('file-uploaded', {
            key: response.data.key,
            filename: this.selectedFile.name,
          });
        } else {
          this.uploadError = 'Upload failed: ' + (response.data.error || 'Unknown error');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        this.uploadError = error.response?.data?.error || 'Failed to upload file';
      } finally {
        this.uploading = false;
        this.selectedFile = null;
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      }
    },

    cancelUpload() {
      this.selectedFile = null;
      this.uploadError = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
  },
};
</script>
